import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
              slideshowElement.warmBoundaryImages?.();
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px'
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  /**
   * Section Rendering API morphs product cards in place (same grid item id).
   * Infinite swipe clones are unkeyed `slideshow-slide`s, so morph remaps them into
   * real slides and leaves scrollLeft at the old clone offset — featured media
   * scrolls off-screen (looks blank until a lazy neighbour loads). Tear clones
   * down and reseat after every morph.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#syncAfterMorph();
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }

    this.#cancelDeferredSwipeClones();

    this.#cloneStart?.remove();
    this.#cloneEnd?.remove();
    this.#cloneStart = undefined;
    this.#cloneEnd = undefined;
    this.removeAttribute('data-clones-ready');
    this.refs.slides?.[0]?.removeAttribute('data-slideshow-initial-target');
    this.refs.scroller?.removeAttribute('data-slideshow-pending-initial');
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });

        // Force Safari to recalculate the timeline state on timeline refresh (after loop)
        requestAnimationFrame(() => {
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id')
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => {
        const selected = i === value;
        el.setAttribute('data-selected', `${selected}`);
        el.removeAttribute('aria-selected');

        const isOptionOrTab = el.getAttribute('role') === 'option' || el.getAttribute('role') === 'tab';
        if (isOptionOrTab) {
          el.setAttribute('aria-selected', `${selected}`);
          return;
        }

        // Decorative dots are spans; only real controls get aria-current.
        if (el instanceof HTMLButtonElement || el.getAttribute('role') === 'button') {
          if (selected) {
            el.setAttribute('aria-current', 'true');
          } else {
            el.removeAttribute('aria-current');
          }
        }
      });
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  /**
   * Whether infinite swipe inserts DOM boundary clones.
   * Nested slideshows and `[no-swipe-clones]` skip cloning (controls can still wrap via `infinite`).
   */
  get #swipeClonesEnabled() {
    return this.infinite && !this.isNested && this.getAttribute('no-swipe-clones') == null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * True while re-seating after a layout/density resize so scroll sync cannot
   * advance `current` to the next slide from stale scrollLeft.
   * @type {boolean}
   */
  #layoutReseating = false;

  /**
   * Slide index pinned across a parent layout/density change. Prefer this over
   * `#current` in ResizeObserver — scroll sync can corrupt `#current` mid-resize.
   * @type {number | null}
   */
  #pinnedLayoutIndex = null;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Synthetic clone of the last slide, prepended before the first slide so that
   * native swipe/scroll gestures have somewhere to go when looping backwards
   * past the start. Undefined when infinite swipe cloning isn't set up.
   * @type {HTMLElement | undefined}
   */
  #cloneStart;

  /**
   * Synthetic clone of the first slide, appended after the last slide so that
   * native swipe/scroll gestures have somewhere to go when looping forwards
   * past the end. Undefined when infinite swipe cloning isn't set up.
   * @type {HTMLElement | undefined}
   */
  #cloneEnd;

  /**
   * Product-card infinite clones are deferred past first paint.
   * @type {boolean}
   */
  #productCardClonesScheduled = false;

  /**
   * @type {AbortController | null}
   */
  #productCardClonesAbort = null;

  /**
   * @type {ReturnType<typeof requestIdleCallback> | number | null}
   */
  #productCardClonesIdleId = null;

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Allow native swipe/scroll gestures to loop past the first/last slide, matching
    // the looping behaviour already provided by the previous/next controls. Must run
    // before the IntersectionObserver is set up so the clones are observed too.
    const { scroller } = this.refs;

    // Product cards + PDP media gallery: keep first paint clone-free (no
    // pending-initial hide). Infinite swipe clones install after idle / user intent.
    const deferClones = this.#shouldDeferSwipeClones;

    if (this.#swipeClonesEnabled && !deferClones) {
      scroller.setAttribute('data-slideshow-pending-initial', '');
      this.#setupInfiniteSwipeClones();
    }

    if (!this.#cloneStart) {
      scroller.removeAttribute('data-slideshow-pending-initial');
    } else {
      // Best-effort seat before the next paint; rAF below re-measures if stale.
      this.#scrollToRealFirstSlide(scroller);
    }

    if (deferClones) {
      this.#scheduleDeferredSwipeClones();
    }

    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
          this.#revealInfiniteSwipeScroller(scroller);
        } else {
          // If a clone was prepended, the browser's default scroll position (0) would land on
          // it instead of the real first slide. Correct that here, now that layout has settled,
          // rather than at insertion time when measurements could still be stale.
          const firstSlide = this.refs.slides?.[0];
          if (this.#cloneStart && firstSlide) {
            this.#scroll?.to(firstSlide, { instant: true });
          }

          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }

          // Reveal only after a second frame so Safari has applied scrollLeft.
          requestAnimationFrame(() => {
            this.#reseatFromStartClone();
            this.#revealInfiniteSwipeScroller(scroller);
          });
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        // Prefer an index pinned before column CSS changed; `#current` may already
        // reflect a wrong mid-resize scrollLeft if sync ran first.
        const indexBeforeResize = this.#pinnedLayoutIndex ?? this.#current;
        this.#layoutReseating = true;
        this.#pinnedLayoutIndex = indexBeforeResize;

        this.#reseatFromStartClone();
        this.#reseatCurrentSlide(indexBeforeResize);

        if (viewTransition.current) await viewTransition.current;

        this.#reseatFromStartClone();
        this.#reseatCurrentSlide(indexBeforeResize);

        requestAnimationFrame(() => {
          this.#reseatCurrentSlide(indexBeforeResize);
          this.#finishLayoutReseating();

          if (visibleSlidesAmount > 1) {
            this.#updateVisibleSlides();
          }

          if (this.hasAttribute('auto-hide-controls')) {
            this.#updateControlsVisibility();
          }
        });
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Defer infinite clones so LCP stays a plain seated track (no pending-initial hide).
   * Product cards and PDP media galleries both paint the first image before JS runs.
   */
  get #shouldDeferSwipeClones() {
    if (!this.#swipeClonesEnabled) return false;
    if (this.classList.contains('product-card-slideshow')) return true;
    return this.closest('media-gallery') != null;
  }

  /**
   * After a Section Rendering morph: drop clone DOM/state and reseat on the
   * featured slide. Safe when clones were never installed.
   * Public so filter/section updates can call it when morph skips Component hooks.
   */
  syncAfterMorph() {
    this.#syncAfterMorph();
  }

  /**
   * After a Section Rendering morph: drop clone DOM/state and reseat on the
   * featured slide. Safe when clones were never installed.
   */
  #syncAfterMorph() {
    const { scroller } = this.refs;
    if (!scroller || !this.isConnected) return;

    // Not initialized yet (e.g. morph during connect) — connectedCallback will set up.
    if (!this.#scroll && (this.slides?.length ?? 0) > 1) return;

    this.#cancelDeferredSwipeClones();
    this.#productCardClonesScheduled = false;

    for (const clone of scroller.querySelectorAll('[data-slideshow-clone]')) {
      clone.remove();
    }

    this.#cloneStart = undefined;
    this.#cloneEnd = undefined;
    this.removeAttribute('data-clones-ready');
    scroller.removeAttribute('data-slideshow-pending-initial');
    this.refs.slides?.[0]?.removeAttribute('data-slideshow-initial-target');

    const index = this.initialSlideIndex || 0;
    this.current = index;

    const slides = this.slides ?? this.refs.slides;
    if (slides) {
      for (const [i, slide] of slides.entries()) {
        slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      }
    }

    // Disable snap while reseating — otherwise scroll-snap keeps the old clone
    // offset and the featured slide stays off-screen after filter morph.
    this.#reseatCurrentSlide(index);
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#reseatCurrentSlide(index);
      scroller.style.scrollSnapType = '';
    });

    if (this.#shouldDeferSwipeClones && (slides?.length ?? 0) > 1) {
      this.#scheduleDeferredSwipeClones();
    }
  }

  #cancelDeferredSwipeClones() {
    this.#productCardClonesAbort?.abort();
    this.#productCardClonesAbort = null;
    if (this.#productCardClonesIdleId != null) {
      if (typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(/** @type {number} */ (this.#productCardClonesIdleId));
      }
      clearTimeout(/** @type {number} */ (this.#productCardClonesIdleId));
      this.#productCardClonesIdleId = null;
    }
  }

  /**
   * Install infinite swipe clones after first paint: on user intent, or idle while in view.
   * Does not use pending-initial (that would hide already-visible LCP media).
   */
  #scheduleDeferredSwipeClones() {
    if (this.#productCardClonesScheduled || this.#cloneStart) return;
    this.#productCardClonesScheduled = true;

    this.#productCardClonesAbort = new AbortController();
    const { signal } = this.#productCardClonesAbort;

    const install = () => {
      if (signal.aborted || !this.isConnected || this.#cloneStart) return;
      this.#cancelDeferredSwipeClones();
      this.#installDeferredSwipeClones();
    };

    this.addEventListener('pointerenter', install, { once: true, signal });
    this.addEventListener('touchstart', install, { once: true, passive: true, signal });
    this.addEventListener('focusin', install, { once: true, signal });

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        if (this.classList.contains('product-card-slideshow')) {
          this.#warmProductCardSlideImages();
        }
        io.disconnect();

        const run = () => install();
        if (typeof requestIdleCallback === 'function') {
          this.#productCardClonesIdleId = requestIdleCallback(run, { timeout: 2500 });
        } else {
          this.#productCardClonesIdleId = window.setTimeout(run, 1200);
        }
      },
      { rootMargin: '100px' }
    );

    signal.addEventListener(
      'abort',
      () => {
        io.disconnect();
      },
      { once: true }
    );

    io.observe(this);
  }

  /**
   * Mount empty boundary clones and reseat without hiding visible media.
   */
  #installDeferredSwipeClones() {
    if (!this.isConnected || this.#cloneStart || !this.#swipeClonesEnabled) return;

    const { scroller } = this.refs;
    if (!scroller) return;

    const index = this.current;
    const slide = this.slides?.[index] ?? this.refs.slides?.[index] ?? this.refs.slides?.[0];

    this.#setupInfiniteSwipeClones();

    if (this.#intersectionObserver) {
      if (this.#cloneStart) this.#intersectionObserver.observe(this.#cloneStart);
      if (this.#cloneEnd) this.#intersectionObserver.observe(this.#cloneEnd);
    }

    if (slide && this.#scroll) {
      this.#scroll.to(slide, { instant: true });
    } else if (slide) {
      scroller.scrollLeft = slide.offsetLeft;
    }

    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      if (slide && this.#scroll) {
        this.#scroll.to(slide, { instant: true });
      } else if (slide) {
        scroller.scrollLeft = slide.offsetLeft;
      }
      this.warmBoundaryImages();
    });
  }

  /**
   * Drop pending-initial once scroll is on a real slide (not while
   * clone deferral is still pending).
   * @param {HTMLElement} scroller
   */
  #revealInfiniteSwipeScroller(scroller) {
    if (this.#shouldDeferSwipeClones && !this.#cloneStart) {
      // Clones not mounted yet — keep the track visible for LCP.
      scroller.removeAttribute('data-slideshow-pending-initial');
      return;
    }

    this.#reseatFromStartClone();
    scroller.removeAttribute('data-slideshow-pending-initial');
    this.warmBoundaryImages();
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    if (this.#layoutReseating) return;

    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id')
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
    this.#warmAdjacentSlideImages();
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
    if (this.#layoutReseating) return;
    this.#resolveInfiniteSwipe();
  };

  /**
   * Prefetch first/last slide images and paint infinite clones as CSS backgrounds.
   * Safari often skips loading off-screen clone <img>s inside overflow scrollers.
   */
  warmBoundaryImages() {
    if (!this.#swipeClonesEnabled) return;

    const slides = this.refs.slides;
    if (!slides?.length) return;

    this.#warmSlideImages(slides[0]);
    this.#warmSlideImages(slides[slides.length - 1]);
    this.#paintBoundaryClones();
  }

  /**
   * Prefetch current slide and neighbors (including wrap ends).
   */
  #warmAdjacentSlideImages() {
    const slides = this.slides;
    if (!slides?.length) return;

    const index = this.current;
    this.#warmSlideImages(slides[index]);
    this.#warmSlideImages(slides[index + 1]);
    this.#warmSlideImages(slides[index - 1]);

    if (this.infinite) {
      if (index <= 0) this.#warmSlideImages(slides[slides.length - 1]);
      if (index >= slides.length - 1) this.#warmSlideImages(slides[0]);
    }

    if (this.classList.contains('product-card-slideshow')) {
      this.#warmSlideImages(slides[index + 2]);
    }
  }

  /**
   * Product cards lazy-load off-screen slides; prefetch the first few once the card is near view.
   */
  #warmProductCardSlideImages() {
    const slides = this.slides;
    if (!slides?.length) return;

    const limit = Math.min(slides.length, 4);
    for (let i = 0; i < limit; i++) {
      this.#warmSlideImages(slides[i]);
    }
  }

  /**
   * @param {HTMLElement | undefined} slide
   */
  #warmSlideImages(slide) {
    if (!slide) return;

    slide.setAttribute('data-slideshow-warm', '');

    for (const img of slide.querySelectorAll('img')) {
      if (!(img instanceof HTMLImageElement)) continue;

      if (img.getAttribute('loading') === 'lazy') {
        img.loading = 'eager';
      }

      this.#preloadImageElement(img);

      if (typeof img.decode === 'function') {
        img.decode().catch(() => {});
      }
    }
  }

  /**
   * Paint start/end clones with CSS backgrounds from detached Image() preloads.
   * Call only after the scroller is seated on a real slide — painted clones must
   * stay CSS-hidden at rest (visible only while transitioning/dragging).
   */
  #paintBoundaryClones() {
    const slides = this.refs.slides;
    const { scroller } = this.refs;
    if (!slides?.length || !this.#cloneStart || !this.#cloneEnd || !scroller) return;
    if (scroller.hasAttribute('data-slideshow-pending-initial')) return;

    const last = slides[slides.length - 1];
    const first = slides[0];

    void this.#paintCloneFromSource(this.#cloneStart, last)
      .then(() => this.#paintCloneFromSource(this.#cloneEnd, first))
      .then(() => {
        if (
          this.#cloneStart?.hasAttribute('data-clone-painted') &&
          this.#cloneEnd?.hasAttribute('data-clone-painted')
        ) {
          // Re-seat once more before advertising ready — Safari can reset scroll
          // while background images decode.
          this.#reseatFromStartClone();
          this.setAttribute('data-clones-ready', '');
        }
      });
  }

  /**
   * @param {HTMLElement} clone
   * @param {HTMLElement | undefined} source
   * @returns {Promise<void>}
   */
  async #paintCloneFromSource(clone, source) {
    if (!source || !clone) return;

    const url = await this.#resolveSlideImageUrl(source);
    if (!url) return;

    // No <img> in clones — Safari won't load off-screen scroller images reliably.
    clone.replaceChildren();
    clone.style.backgroundImage = `url(${JSON.stringify(url)})`;
    clone.style.backgroundSize = 'cover';
    clone.style.backgroundPosition = 'center';
    clone.style.backgroundRepeat = 'no-repeat';
    clone.setAttribute('data-clone-painted', '');

    // Do not rewrite source slide `img.src` — assigning a preloader URL (often a
    // different width than the in-page srcset pick) flashes/reloads the LCP image.
  }

  /**
   * @param {HTMLElement} slide
   * @returns {Promise<string | null>}
   */
  #resolveSlideImageUrl(slide) {
    const img = slide.querySelector('img');
    if (!(img instanceof HTMLImageElement)) return Promise.resolve(null);

    return this.#preloadImageElement(img);
  }

  /**
   * @param {HTMLImageElement} img
   * @returns {Promise<string | null>}
   */
  #preloadImageElement(img) {
    const fallback = img.currentSrc || img.src || null;
    if (!fallback) return Promise.resolve(null);

    if (img.complete && img.naturalWidth > 0 && img.currentSrc) {
      return Promise.resolve(img.currentSrc);
    }

    return new Promise((resolve) => {
      const preloader = new Image();
      const slide = img.closest('slideshow-slide');
      const width = Math.ceil(slide?.getBoundingClientRect().width || img.getBoundingClientRect().width || 0);
      // Concrete sizes — Safari resolves srcset poorly with `sizes="auto, …"` on detached Image.
      preloader.sizes =
        width > 0 ? `${width}px` : (img.getAttribute('sizes') || '').replace(/^\s*auto\s*,?\s*/i, '') || '50vw';

      preloader.onload = () => resolve(preloader.currentSrc || fallback);
      preloader.onerror = () => resolve(fallback);

      if (img.srcset) preloader.srcset = img.srcset;
      preloader.src = img.src || fallback;
    });
  }

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = this.#realIndexOf(closestVisibleSlide);
    if (index === -1) return this.current;

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id')
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
    if (this.classList.contains('product-card-slideshow')) {
      this.#warmProductCardSlideImages();
    }
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : [])
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        // Sort by DOM order rather than `slides` index, since boundary clones (see
        // #setupInfiniteSwipeClones) aren't part of `slides` but still need ordering.
        this.#visibleSlides.sort((a, b) => {
          const position = a.compareDocumentPosition(b);
          if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
          if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
          return 0;
        });
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px'
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });

    // Also observe the boundary clones (if any) so the current index updates as soon as a
    // swipe/scroll crosses into one, instead of waiting for the gesture to fully settle.
    if (this.#cloneStart) this.#intersectionObserver.observe(this.#cloneStart);
    if (this.#cloneEnd) this.#intersectionObserver.observe(this.#cloneEnd);
  }

  /**
   * Resolves the real slide index for an element, translating the synthetic boundary clones
   * (see #setupInfiniteSwipeClones) to the index of the real slide they stand in for.
   * @param {HTMLElement} slide
   * @returns {number} The real slide index, or -1 if not found.
   */
  #realIndexOf(slide) {
    if (slide === this.#cloneStart) return this.slides ? this.slides.length - 1 : -1;
    if (slide === this.#cloneEnd) return 0;

    return this.slides?.indexOf(slide) ?? -1;
  }

  /**
   * Prepends a clone of the last slide and appends a clone of the first slide so that native
   * swipe/scroll/trackpad gestures have somewhere to scroll to when going past the start or end
   * of the slideshow. Once the gesture settles on a clone, {@link #resolveInfiniteSwipe} silently
   * jumps the scroll position to the equivalent real slide, making the loop invisible to the user.
   *
   * This only inserts the clones into the DOM. The initial scroll position is corrected later,
   * once layout has settled (see the `requestAnimationFrame` in #setupSlideshow), to avoid
   * measuring stale offsets which previously caused the wrong slide to flash in on load.
   *
   * Skipped for nested slideshows (which don't scroll natively) and for slideshows with slides
   * that can be dynamically hidden/revealed (e.g. variant-filtered galleries), since the clones
   * would need to be kept in sync with those changes.
   */
  #setupInfiniteSwipeClones() {
    if (!this.#swipeClonesEnabled) return;

    const { slides, scroller } = this.refs;
    if (!slides || slides.length <= 1) return;
    if (slides.some((slide) => slide.hasAttribute('hidden'))) return;

    const first = slides[0];
    const last = slides[slides.length - 1];
    if (!first || !last) return;

    const cloneStart = this.#createInfiniteSwipeClone(last, 'start');
    const cloneEnd = this.#createInfiniteSwipeClone(first, 'end');

    scroller.prepend(cloneStart);
    scroller.append(cloneEnd);

    first.setAttribute('data-slideshow-initial-target', '');

    this.#cloneStart = cloneStart;
    this.#cloneEnd = cloneEnd;
  }

  /**
   * Builds a boundary clone that keeps the source slide's box size but strips media.
   * Image-bearing clones flash the wrong frame whenever scrollLeft is briefly 0.
   *
   * @param {HTMLElement} source
   * @param {'start' | 'end'} position
   * @returns {HTMLElement}
   */
  #createInfiniteSwipeClone(source, position) {
    const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
    clone.removeAttribute('ref');
    clone.removeAttribute('slide-id');
    clone.removeAttribute('data-slideshow-initial-target');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.setAttribute('data-slideshow-clone', position);

    // Keep aspect/layout classes; drop media so nothing wrong can paint or decode.
    clone.replaceChildren();

    return clone;
  }

  /**
   * Instantly seats the scroller on the real first slide (after the start clone).
   * @param {HTMLElement} [scroller]
   */
  #scrollToRealFirstSlide(scroller = this.refs.scroller) {
    const firstSlide = this.refs.slides?.[0];
    if (!this.#cloneStart || !firstSlide || !scroller) return;

    if (this.#scroll) {
      this.#scroll.to(firstSlide, { instant: true });
      return;
    }

    const axis = scroller.scrollWidth > scroller.clientWidth ? 'x' : 'y';
    const edge = axis === 'x' ? 'Left' : 'Top';
    scroller[`scroll${edge}`] = firstSlide[`offset${edge}`];
  }

  /**
   * Re-seats on the real first slide when Safari (or a layout resize) parks scroll on the start clone.
   */
  #reseatFromStartClone() {
    if (!this.#cloneStart || this.current !== 0 || this.#disabled) return;

    const { scroller } = this.refs;
    const firstSlide = this.refs.slides?.[0];
    if (!scroller || !firstSlide || !this.#scroll) return;

    const edge = this.#scroll.axis === 'x' ? 'Left' : 'Top';
    const onClone = Math.abs(scroller[`scroll${edge}`] - this.#cloneStart[`offset${edge}`]) <= 1;
    if (!onClone) return;

    this.#scroll.to(firstSlide, { instant: true });
  }

  /**
   * Keep the current slide after a parent layout/density change.
   * Call from results-list with the index captured BEFORE column CSS changes.
   *
   * @param {number} [forcedIndex]
   */
  reseatAfterLayoutChange(forcedIndex) {
    const index = Number.isFinite(forcedIndex) ? /** @type {number} */ (forcedIndex) : this.#current;
    this.#pinnedLayoutIndex = index;
    this.#layoutReseating = true;
    void this.refs.scroller?.offsetWidth;
    this.#reseatFromStartClone();
    this.#reseatCurrentSlide(index);

    // Keep snap disabled and sync blocked until VT finishes (ResizeObserver
    // also reseats). Do not clear `#layoutReseating` on a short rAF — that let
    // scroll sync pin the wrong slide, then RO locked it in.
    const finish = async () => {
      if (viewTransition.current) await viewTransition.current;
      this.#reseatCurrentSlide(index);
      requestAnimationFrame(() => {
        this.#reseatCurrentSlide(index);
        this.#finishLayoutReseating();
      });
    };
    void finish();
  }

  #finishLayoutReseating() {
    const { scroller } = this.refs;
    if (scroller) {
      scroller.style.scrollSnapType = '';
    }
    this.#layoutReseating = false;
    this.#pinnedLayoutIndex = null;
  }

  /**
   * Re-seat on the logical current slide after the track width changes
   * (product-grid density toggles). Without clones, scrollLeft stays in px while
   * slide widths change — which lands on the next image.
   *
   * Leaves scroll-snap off while `#layoutReseating` — restoring snap mid-settle
   * re-pulls to a neighbor and corrupts the index.
   *
   * @param {number} [forcedIndex] - Index captured before layout/view-transition settles
   */
  #reseatCurrentSlide(forcedIndex) {
    if (this.#disabled) return;
    if (this.hasAttribute('dragging')) return;

    const { scroller } = this.refs;
    if (!scroller) return;

    const index = Number.isFinite(forcedIndex) ? /** @type {number} */ (forcedIndex) : this.current;
    const slide = this.slides?.[index] ?? this.refs.slides?.[index];
    if (!slide) return;

    if (this.#current !== index) {
      this.current = index;
    }
    this.removeAttribute('transitioning');

    scroller.style.scrollSnapType = 'none';
    void scroller.offsetWidth;

    if (this.#scroll) {
      this.#scroll.to(slide, { instant: true });
    } else {
      scroller.scrollLeft = slide.offsetLeft;
    }
  }

  /**
   * Detects whether a swipe/scroll/drag gesture has come to rest on one of the synthetic
   * boundary clones and, if so, instantly jumps to the equivalent real slide so the loop
   * is imperceptible to the user.
   */
  #resolveInfiniteSwipe() {
    const cloneStart = this.#cloneStart;
    const cloneEnd = this.#cloneEnd;
    if (!cloneStart && !cloneEnd) return;
    if (!this.#scroll) return;

    const { slides } = this;
    if (!slides?.length) return;

    const { scroller } = this.refs;
    const edge = this.#scroll.axis === 'x' ? 'Left' : 'Top';
    const scrollPosition = scroller[`scroll${edge}`];
    const tolerance = 1;

    if (cloneStart && Math.abs(scrollPosition - cloneStart[`offset${edge}`]) <= tolerance) {
      const lastIndex = slides.length - 1;
      const lastSlide = slides[lastIndex];
      if (!lastSlide) return;

      this.#jumpToWrappedSlide(lastSlide, lastIndex);
      return;
    }

    if (cloneEnd && Math.abs(scrollPosition - cloneEnd[`offset${edge}`]) <= tolerance) {
      const firstSlide = slides[0];
      if (!firstSlide) return;

      this.#jumpToWrappedSlide(firstSlide, 0);
    }
  }

  /**
   * Instantly scrolls to the real slide equivalent of a boundary clone and updates state to
   * match, without animating (the clone and the real slide are pixel-identical, so the jump
   * is invisible).
   * @param {HTMLElement} slide - The real slide to jump to.
   * @param {number} index - The index of the slide to jump to.
   */
  #jumpToWrappedSlide(slide, index) {
    const previousIndex = this.current;

    this.#warmSlideImages(slide);
    this.#scroll?.to(slide, { instant: true });
    this.current = index;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id')
      })
    );
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center'
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
